import { createReducer } from "typesafe-actions";

import { EntityType } from "@mapmycustomers/shared/enum";

import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import EntityTypesSupportedInFrequencyCoverage from "@app/types/dashboard/cards/frequencyCoverage/EntityTypesSupportedInFrequencyCoverage";
import FrequencyCoverageCardConfiguration from "@app/types/dashboard/cards/frequencyCoverage/FrequencyCoverageCardConfiguration";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";
import { createEmptyFilterModel } from "@app/util/filters/utils";

import {
  applyFrequencyCoverageDrillDownListViewSettings,
  fetchFrequencyCoverageCardDrillDownData,
  FrequencyCoverageCardActions,
  hideFrequencyCoverageDrillDown,
  showFrequencyCoverageDrillDown,
} from "./actions";

export interface FrequencyCoverageCardState {
  configuration?: FrequencyCoverageCardConfiguration; // also works as a "visible" trigger, visible if set
  loading?: boolean;
  totalFilteredRecords: Record<EntityTypesSupportedInFrequencyCoverage, number>;
  totalRecords: Record<EntityTypesSupportedInFrequencyCoverage, number>;
  viewStates: Record<EntityTypesSupportedInFrequencyCoverage, DrillDownViewState>;
}

export const frequencyCoverageInitialState: FrequencyCoverageCardState = {
  totalFilteredRecords: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => ({ ...result, [entityType]: 0 }),
    {} as Record<EntityTypesSupportedInFrequencyCoverage, number>
  ),
  totalRecords: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => ({ ...result, [entityType]: 0 }),
    {} as Record<EntityTypesSupportedInFrequencyCoverage, number>
  ),
  viewStates: ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).reduce(
    (result, entityType) => {
      result[entityType] = {
        columns: [],
        filter: createEmptyFilterModel(),
        range: { endRow: DEFAULT_PAGE_SIZE, startRow: 0 },
        sort: [],
      };
      return result;
    },
    {} as Record<EntityTypesSupportedInFrequencyCoverage, DrillDownViewState>
  ),
};

const frequencyCoverageState = createReducer<
  FrequencyCoverageCardState,
  FrequencyCoverageCardActions
>(frequencyCoverageInitialState)
  .handleAction(showFrequencyCoverageDrillDown.request, (state, { payload }) => ({
    ...state,
    configuration: payload.configuration,
  }))
  .handleAction(showFrequencyCoverageDrillDown.success, (state, { payload }) => ({
    ...state,
    viewStates: payload.viewStates,
  }))
  .handleAction(hideFrequencyCoverageDrillDown, (state) => ({ ...state, configuration: undefined }))
  .handleAction(fetchFrequencyCoverageCardDrillDownData.request, (state) => ({
    ...state,
    loading: true,
  }))
  .handleAction(fetchFrequencyCoverageCardDrillDownData.success, (state, { payload }) => ({
    ...state,
    loading: false,
    totalFilteredRecords: {
      ...state.totalFilteredRecords,
      [payload.entityType]: payload.totalFilteredRecords,
    },
    totalRecords: {
      ...state.totalRecords,
      [payload.entityType]: payload.totalRecords,
    },
  }))
  .handleAction(fetchFrequencyCoverageCardDrillDownData.failure, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(applyFrequencyCoverageDrillDownListViewSettings, (state, { payload }) => {
    const viewState = state.viewStates[payload.entityType];
    return {
      ...state,
      viewStates: {
        ...state.viewStates,
        [payload.entityType]: {
          ...viewState,
          columns: payload.columns ?? viewState.columns,
          filter: payload.filter ?? viewState.filter,
          range: payload.range ?? viewState.range,
          sort: payload.sort ?? viewState.sort,
        },
      },
    };
  });

export default frequencyCoverageState;
